// Space outside the ship, in kilometres. The ship's interior is its own frame (decks stay level for the crew, under the
// ship's gravity); outside, the ship has a position and an orientation, and everything seen on the screens and from
// outside is placed relative to them.
//
// Ship frame (the same axes as the browser scene): +x is the bow, +y is up, -z is starboard (Blender +Y, the Comms side).
// Orientation is heading (yaw, 0-360, clockwise toward starboard), mark (pitch, nose up positive, -89..89) and roll
// (starboard side down positive, -180..180), applied in that order.

import type { Vec3 } from "./ship.js";
import { bodiesOf, nearestBody, nearestSystem, type Body } from "./universe.js";

export const FULL_IMPULSE_KMS = 10;                                                                     // full impulse, km/s
export const MIN_ALTITUDE_KM = 25;                                                                      // the ship holds at this height

export function norm(v: Vec3): Vec3 { const l = Math.hypot(v.x, v.y, v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; }
const rad = (d: number) => (d * Math.PI) / 180, deg = (r: number) => (r * 180) / Math.PI;

// the rotation from ship frame to world frame, as a 3x3 row-major matrix
export function orientation(heading: number, mark: number, roll: number): number[] {
  const y = rad(heading), p = rad(mark), r = rad(roll);
  const cy = Math.cos(y), sy = Math.sin(y), cp = Math.cos(p), sp = Math.sin(p), cr = Math.cos(r), sr = Math.sin(r);
  // Ryaw (about +y, bow toward starboard, -z) * Rpitch (in the bow-up plane, nose up) * Rroll (about the bow, starboard side down)
  const Ry = [cy, 0, sy, 0, 1, 0, -sy, 0, cy], Rp = [cp, -sp, 0, sp, cp, 0, 0, 0, 1], Rr = [1, 0, 0, 0, cr, sr, 0, -sr, cr];
  return mul3(mul3(Ry, Rp), Rr);
}
// a turn by the ship's own axes (the pilot's: yaw toward her starboard, pitch toward her overhead, roll about her bow, degrees),
// as the heading, mark and roll it leaves her at. Flying by hand turns her this way, so a starboard turn is to her starboard
// however she lies (Grey, 2026-10-29: rolled over, the stick's starboard turned her to port). null: it would carry her over
// the vertical (past the mark limit), where heading and roll lose their meaning
export function turnBody(heading: number, mark: number, roll: number, yaw: number, pitch: number, rollBy: number, markLimit = 89): { heading: number; mark: number; roll: number } | null {
  const y = rad(yaw), p = rad(pitch), r = rad(rollBy);
  const Ry = [Math.cos(y), 0, Math.sin(y), 0, 1, 0, -Math.sin(y), 0, Math.cos(y)], Rp = [Math.cos(p), -Math.sin(p), 0, Math.sin(p), Math.cos(p), 0, 0, 0, 1], Rr = [1, 0, 0, 0, Math.cos(r), Math.sin(r), 0, -Math.sin(r), Math.cos(r)];
  const R0 = orientation(heading, mark, roll), R = mul3(mul3(mul3(R0, Ry), Rp), Rr);
  const m = deg(Math.asin(Math.max(-1, Math.min(1, R[3])))); if (Math.abs(m) > markLimit) return null;
  if (R0[0] * R[0] + R0[6] * R[6] < 0) return null;                    // the bow's level heading turned back on itself: over the top

  return { heading: (deg(Math.atan2(-R[6], R[0])) + 360) % 360, mark: m, roll: deg(Math.atan2(R[5], R[4])) };
}
function mul3(a: number[], b: number[]): number[] {
  const o = new Array(9).fill(0); for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) for (let k = 0; k < 3; k++) o[i * 3 + j] += a[i * 3 + k] * b[k * 3 + j]; return o;
}
export const toWorld = (R: number[], v: Vec3): Vec3 => ({ x: R[0] * v.x + R[1] * v.y + R[2] * v.z, y: R[3] * v.x + R[4] * v.y + R[5] * v.z, z: R[6] * v.x + R[7] * v.y + R[8] * v.z });
export const toShip = (R: number[], v: Vec3): Vec3 => ({ x: R[0] * v.x + R[3] * v.y + R[6] * v.z, y: R[1] * v.x + R[4] * v.y + R[7] * v.z, z: R[2] * v.x + R[5] * v.y + R[8] * v.z });

// a direction in the world as a heading and mark to point the bow at it
export function aimAt(dir: Vec3): { heading: number; mark: number } {
  const d = norm(dir); return { heading: (deg(Math.atan2(-d.z, d.x)) + 360) % 360, mark: deg(Math.asin(Math.max(-1, Math.min(1, d.y)))) };
}
// where a world direction sits relative to the ship: bearing (clockwise from the bow) and mark (up from the deck plane)
export function relative(R: number[], dir: Vec3): { bearing: number; mark: number } {
  const s = norm(toShip(R, dir)); return { bearing: (deg(Math.atan2(-s.z, s.x)) + 360) % 360, mark: deg(Math.asin(Math.max(-1, Math.min(1, s.y)))) };
}
// the nearest planet or moon: its direction, range to its surface, and the body itself
export function planetFrom(pos: Vec3): { dir: Vec3; range: number; altitude: number; body: Body } {
  const b = nearestBody(pos), d = { x: b.centre.x - pos.x, y: b.centre.y - pos.y, z: b.centre.z - pos.z }, dist = Math.hypot(d.x, d.y, d.z);
  return { dir: norm(d), range: dist - b.radius, altitude: dist - b.radius, body: b };
}
// the world that fills the most sky from here (the largest apparent size), the one the screens draw close up. Not simply the
// nearest surface: from a high orbit of Mars, little Phobos or Deimos (11 and 6 km) swing nearer than Mars's surface for
// ten seconds at a time, and drawing them in her place made Mars vanish and come back.
export function planetInView(pos: Vec3): { dir: Vec3; range: number; altitude: number; body: Body } {
  const sys = nearestSystem(pos); let b: Body | null = null, best = -1;
  for (const p of bodiesOf(sys.id)) for (const q of [p, ...p.moons]) {
    const d = Math.hypot(q.centre.x - pos.x, q.centre.y - pos.y, q.centre.z - pos.z), size = q.radius / Math.max(d, q.radius);
    if (size > best) { best = size; b = q; }
  }
  if (!b) return planetFrom(pos);
  const d = { x: b.centre.x - pos.x, y: b.centre.y - pos.y, z: b.centre.z - pos.z }, dist = Math.hypot(d.x, d.y, d.z);
  return { dir: norm(d), range: dist - b.radius, altitude: dist - b.radius, body: b };
}
// a second world worth drawing beside the one in view: the next biggest-looking (a moon by its planet, the planet over a
// moon you're visiting), if it is more than a dot (about a tenth of a degree across)
export function secondInView(pos: Vec3, first: Body): Body | null {
  const sys = nearestSystem(pos); let b: Body | null = null, best = 0.0009;
  for (const p of bodiesOf(sys.id)) for (const q of [p, ...p.moons]) {
    if (q === first) continue;
    const d = Math.hypot(q.centre.x - pos.x, q.centre.y - pos.y, q.centre.z - pos.z), size = q.radius / Math.max(d, q.radius);
    if (size > best) { best = size; b = q; }
  }
  return b;
}
// the star of the system the ship is in: its direction and distance
export function starFrom(pos: Vec3) {
  const s = nearestSystem(pos), d = { x: s.pos.x - pos.x, y: s.pos.y - pos.y, z: s.pos.z - pos.z }, dist = Math.hypot(d.x, d.y, d.z);
  return { dir: norm(d), dist, system: s };
}
